import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import HomePage from '../../presentation/page/HomePage';
import { AddPatientPageDomain } from '../../domain/page/patiente/AddPatientPageDomain';
import { ListPatientPageDomain } from '../../domain/page/patiente/ListPatientPageDomain';
import { PrivateRoutes } from '../constant/RouteConstant';
import { AddProfessionalPageDomain } from '../../domain/page/professional/AddProfessionalPageDomain';
import { ListProfessionalPageDomain } from '../../domain/page/professional/ListProfessionalPageDomain';
import { ChangePasswordPageDomain } from '../../domain/page/password/ChangePasswordPageDomain';
import { EditClinicPageDomain } from '../../domain/page/clinic/EditClinicPageDomain';
import { ListAppointmentPageDomain } from '../../domain/page/appointment/ListAppointmentPageDomain';
import ErrorAuthenticationTokenPage from '../../presentation/page/error/ErrorAuthenticationTokenPage';
import ErrorAccessDeniedPage from '../../presentation/page/error/ErrorAccessDeniedPage';
import { useContext } from 'react';
import { AuthContext } from '../../data/context/AuthContext';
import { ProfilesConstant } from '../constant/ProfilesConstant';

/**
 * Rota privada que verifica se o usuário tem permissão para acessar
 * @param children
 * @param requiredRoles
 * @constructor
 */
const ProtectedRoute = ({ children, requiredRoles }: { children: JSX.Element; requiredRoles: string[] }) => {
  const { loggedUserData } = useContext(AuthContext); // Acessa o contexto

  const userRoles = loggedUserData?.roles || [];
  const access = requiredRoles.some((role) => userRoles.includes(role));

  if (!access) {
    return <Navigate to={PrivateRoutes.ACCESS_DENIED} />;
  }

  return children;
};

/**
 * PrivateRoute - conjunto de rotas autenticadas
 * @constructor
 */
export function PrivateRoute() {
  /**
   * Perfil de acesso
   */
  const PROFILE_ADMIN = ProfilesConstant.admin;
  const PROFILE_DENTISTA = ProfilesConstant.dentist;
  const PROFILE_RECEPCIONISTA = ProfilesConstant.receptionist;

  return (
    <BrowserRouter>
      <Routes>
        <Route path={PrivateRoutes.HOME} element={<HomePage />} />

        <Route
          path={PrivateRoutes.CHANGE_PASSWORD}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_DENTISTA, PROFILE_RECEPCIONISTA]}>
              <ChangePasswordPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={PrivateRoutes.EDIT_CLINIC}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN]}>
              <EditClinicPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${PrivateRoutes.ADD_PROFESSIONAL}/:id?`}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_RECEPCIONISTA]}>
              <AddProfessionalPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={PrivateRoutes.LIST_PROFESSIONAL}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_RECEPCIONISTA]}>
              <ListProfessionalPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={`${PrivateRoutes.ADD_PATIENT}/:id?`}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_DENTISTA, PROFILE_RECEPCIONISTA]}>
              <AddPatientPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={PrivateRoutes.LIST_PATIENT}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_DENTISTA, PROFILE_RECEPCIONISTA]}>
              <ListPatientPageDomain />
            </ProtectedRoute>
          }
        />

        <Route
          path={PrivateRoutes.LIST_APPOINTMENT}
          element={
            <ProtectedRoute requiredRoles={[PROFILE_ADMIN, PROFILE_DENTISTA, PROFILE_RECEPCIONISTA]}>
              <ListAppointmentPageDomain />
            </ProtectedRoute>
          }
        />

        <Route path={PrivateRoutes.TOKEN_EXPIRED} element={<ErrorAuthenticationTokenPage />} />
        <Route path={PrivateRoutes.ACCESS_DENIED} element={<ErrorAccessDeniedPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default PrivateRoute;
